import { useQuery } from "@tanstack/react-query";
import { router } from "expo-router";
import { observer } from "mobx-react";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { StyleSheet } from "react-native";
import { Spacer } from "~/components/Spacer";
import { ButtonV2 } from "~/components/V2/Button";
import { Card } from "~/components/V2/Card";
import { ErrorViewV2 } from "~/components/V2/ErrorViewV2";
import { SpinnerV2 } from "~/components/V2/SpinnerV2";
import { BrandText } from "~/components/V2/Text/BrandText";
import { View } from "~/components/V2/View";
import { useEvent } from "~/hooks/useEvent";
import { useStore } from "~/mobx/utils/useStore";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { bookingDataLayerPush } from "~/utils/logBookingEvent";
import { useStandaloneBookingFlow } from "./useStandaloneBookingFlow";
import { withBookingData } from "./withBookingData";

const SelectPractitionerScreen = observer(() => {
  const { isStandAloneBookingFlow, isNewUser } = useStandaloneBookingFlow();
  const store = useStore();
  const [selectedPractitioner, setSelectedPractitioner] = useState<string>();

  const clinicId = store.bookingStore.clinicId;
  const reasonId = store.bookingStore.reasonId;

  const query = useQuery({
    queryKey: ["practitioners", { clinicId, reasonId }],
    queryFn: async () => {
      const { practitioners } =
        await store.timeslotsStore.getAvailability(null);

      return { practitioners };
    },
  });

  const practitioners = query.data?.practitioners
    ? query.data.practitioners
    : {};

  const data = Object.keys(practitioners).filter((key) => key !== "areVisible");

  const navigateToNextScreen = useEvent(async () => {
    if (!selectedPractitioner) {
      return;
    }

    const practitionerId = practitioners[selectedPractitioner];

    store.bookingStore.setPractitionerId(practitionerId);

    if (isStandAloneBookingFlow) {
      bookingDataLayerPush({
        event: "Booking_web_app",
        ga_action: "Clinician selected",
        practitionerId: practitionerId ?? "",
      });
      router.navigate({
        pathname: `/(standalone)/booking/date-and-time`,
        params: {
          clinicId,
          isNewUser: isNewUser ? "1" : "0",
        },
      });
    } else {
      router.navigate({ pathname: `/(app)/book-appointment/date-and-time` });
    }
  });

  if (query.isLoading) {
    return (
      <View centerContent style={{ paddingTop: 60 }}>
        <SpinnerV2 />
      </View>
    );
  }

  if (query.isError) {
    return (
      <View flex colorWhite>
        <View centerContent paddingHorizontal24>
          <ErrorViewV2
            error={query.error}
            title={intl.formatMessage({
              id: "HYP1NC",
              defaultMessage: "Something went wrong",
              description: "Error view title",
            })}
          />
          <ButtonV2
            onPress={query.refetch}
            title={intl.formatMessage({
              id: "ghblYR",
              defaultMessage: "Try Again!",
              description: "Button to retry fetching clinic list",
            })}
          />
        </View>
      </View>
    );
  }

  return (
    <View flex colorWhite padding16>
      <View paddingVertical40 alignItemsCenter>
        <BrandText sizeH1 weightExtraBold colorBlack alignCenter>
          <FormattedMessage
            id="a5fQrr"
            defaultMessage="Do you want to choose a specific healthcare provider?"
            description="Title on the clinician select screen"
          />
        </BrandText>
      </View>
      {data.map((vet: string) => (
        <>
          <Card
            title={vet}
            subtitle=""
            onPress={() => {
              setSelectedPractitioner((prev) =>
                prev === vet ? undefined : vet,
              );
            }}
            isRadio
            isChecked={selectedPractitioner === vet}
            titleStyle={S.vetNameText}
            containerStyle={S.cardStyle}
          />
          <Spacer />
        </>
      ))}

      <View flex flexDirectionColumnReverse paddingHorizontal24 colorWhite>
        <ButtonV2
          medium
          title={intl.formatMessage({
            id: "uGMKxR",
            defaultMessage: "Confirm",
            description: "Confirm button",
          })}
          onPress={navigateToNextScreen}
          disabled={!selectedPractitioner}
        />
      </View>
    </View>
  );
});

export const SelectPractitionerScreenWithBookingData = withBookingData(
  SelectPractitionerScreen,
  ["clinicId", "patientId", "reasonId"],
);

const S = StyleSheet.create({
  vetNameText: {
    fontSize: styleConstantsV2.fontSizeDesktopH4,
    color: "#333333",
  },
  cardStyle: {
    paddingVertical: styleConstantsV2.spacing16,
  },
});
